import { formatStringForTemplating } from "@/backend/helpers/utils";
import { Parameter } from "@/backend/models/parameter";
import { buildSelectableParameters, useGetParametersToAssignFromInput, useGetParametersToAssignFromOutput, useParseProcedureReferences } from "@/frontend/hooks/parameters";
import { useParameterStore } from "@/frontend/store/ParameterStore";
import { I18n } from "@/i18n/I18n";
import { CUSTOM_FIELD_TYPES, TEXT_CUSTOM_FIELD_TYPES } from "@/types/customFieldTypes";
import { DataTypes } from "@/types/itemReferenceTypes";
import { ParameterType, ParameterInputType, ParameterSubjectType, ParameterReply, SelectableParameter } from "@/types/parameterTypes";
import { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { NonPrimitiveParameterModal } from "./NonPrimitiveParameterModal";
import { BasicButton } from "../buttons/BasicButton";
import { defaultTypeOptions, FieldTypeSelect } from "./FieldTypeSelect";
import { InputTypeSelect } from "./InputTypeSelect";
import { useTreeStore } from "@/frontend/store/TreeStore";
import { useShallow } from "zustand/react/shallow";
import { GenericParameterSelect } from "./GenericParameterSelect";
import { Operation } from "@/backend/models/operation";
import { TitleAndIdentifierAndDescriptionFormInputs } from "./TitleAndIdentifierAndDescriptionFormInputs";

export function ParameterAddButton(props: {
  subjectId: string,
  parameterType: ParameterType,
  defaultInputType?: ParameterInputType,
  subjectType: ParameterSubjectType,
  customFieldType?: CUSTOM_FIELD_TYPES,
  onCreate?: (response: ParameterReply) => void
}) {
  const { subjectId, parameterType, defaultInputType, subjectType, customFieldType, onCreate } = props;
  const operation = useTreeStore(state => state.operations.find(op => op.id === subjectId));
  const step = useTreeStore(useShallow(state => state.containerLookup[subjectId]));
  // const procedure = useProcedureStore(state => state.procedure);
  const parameterIdentifierInvalid = useParameterStore(state => state.parameterIdentifierInvalid);
  const createParameter = useParameterStore(state => state.createParameter);
  const createReference = useParameterStore(state => state.createReference);
  const parseProcedureReferences = useParseProcedureReferences({ rangeParameterId: '', parentId: subjectId });
  const [showParameterModal, setShowParameterModal] = useState<boolean>(false);
  const [title, setTitle] = useState('');
  const [identifier, setIdentifier] = useState('');
  const allowedFieldTypes = subjectType === ParameterSubjectType.PART ? TEXT_CUSTOM_FIELD_TYPES : defaultTypeOptions;
  const [fieldType, setFieldType] = useState<CUSTOM_FIELD_TYPES | null>(null);
  const [titleError, setTitleError] = useState<string>();
  const [identifierError, setIdentifierError] = useState<string>();
  const [fieldTypeError, setFieldTypeError] = useState<string>();
  const [inputType, setInputType] = useState<ParameterInputType>(defaultInputType || ParameterInputType.FREE_FORM);
  const [linkedOutput, setLinkedOutput] = useState<SelectableParameter | undefined>(undefined);
  const [newParameterId, setNewParameterId] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [allowChoice, setAllowChoice] = useState(true);

  const handleSave = async () => {
    if (!title) {
      setTitleError(I18n.get('requiredFieldError', { field: I18n.get('title') }));
    }
    if (!fieldType && !customFieldType) {
      setFieldTypeError(I18n.get('requiredFieldError', { field: I18n.get('fieldType') }));
    }
    if (!identifier && subjectType === ParameterSubjectType.OPERATION) {
      setIdentifierError(I18n.get('requiredFieldError', { field: I18n.get('identifier') }));
    }
    if (!title || (!identifier && subjectType === ParameterSubjectType.OPERATION) || (!fieldType && !customFieldType)) {
      return;
    }
    const request = {
      title,
      fieldType: fieldType || customFieldType as CUSTOM_FIELD_TYPES,
      position: 0,
      subjectId,
      subjectType,
      type: parameterType,
      inputType,
      identifier,
      description,
    };
    const response = await createParameter(request);
    if (response.parameter) {
      if (linkedOutput) {
        const reference = {
          dataConsumerId: response.parameter.id,
          dataConsumerType: DataTypes.Parameter,
          dataSourceId: linkedOutput.id,
          dataSourceType: DataTypes.Parameter,
          sourceObjectKeyId: linkedOutput.keyId,
        };
        await createReference(reference);
      }
      if (new Parameter(response.parameter).fieldType === CUSTOM_FIELD_TYPES.Object && !step?.container?.isRepeatingStep() && !linkedOutput) {
        setNewParameterId(response.parameter.id);
      }
      onCreate?.(response);
    }
    handleClose();
  };

  const setTitleWrapper = (value: string) => {
    setTitle(value);
    setTitleError(parseProcedureReferences(value) || '');
  };

  const onFieldSelect = (selectedFieldType: CUSTOM_FIELD_TYPES) => {
    setFieldType(selectedFieldType);
    setFieldTypeError('');

    if (
      parameterType === ParameterType.OUTPUT
      && subjectType === ParameterSubjectType.OPERATION
      && selectedFieldType === CUSTOM_FIELD_TYPES.File
    ) {
      setAllowChoice(false);

      if (inputType === ParameterInputType.SINGLE_CHOICE || inputType === ParameterInputType.MULTIPLE_CHOICE) {
        setInputType(ParameterInputType.FREE_FORM);
      }
    } else {
      setAllowChoice(true);
    }
  };

  const onInputTypeSelect = (selectedInputType: ParameterInputType) => {
    setInputType(selectedInputType);
    if (selectedInputType === ParameterInputType.TABLE) {
      onFieldSelect(CUSTOM_FIELD_TYPES.Object);
    }
  };

  const handleOutputSelect = (param: SelectableParameter | undefined) => {
    setLinkedOutput(param);
    if (param) {
      setFieldType(param.fieldType);
      setInputType(param.inputType);
      if (!title) {
        setTitleWrapper(param.title);
        autoSetIdentifier(param.title);
      }
      setFieldTypeError('');
    }
  };

  const handleClose = () => {
    setShowParameterModal(false);
    setTitle('');
    setDescription('');
    setIdentifier('');
    setFieldType(null);
    setTitleError('');
    setFieldTypeError('');
    setLinkedOutput(undefined);
    setInputType(defaultInputType || ParameterInputType.FREE_FORM);
    setIdentifierError(undefined);
  };

  const autoSetIdentifier = (newTitle: string) => {
    if (identifier) return;
    const newIdentifier = formatStringForTemplating(newTitle);
    if (!parameterIdentifierInvalid({ identifier: newIdentifier, subjectId, subjectType, type: parameterType })) {
      setIdentifier(newIdentifier);
      setIdentifierError('');
    }
  };

  return (
  <>
    <div className='d-flex'>
      <BasicButton
        className='btn btn-sm btn-primary'
        label={I18n.get('addParameter', {type: I18n.get(parameterType)})}
        onClick={() => setShowParameterModal(true)}
      />
    </div>
    {showParameterModal &&  <Modal show onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>{I18n.get('createParameter', { type: I18n.get(parameterType) })}</Modal.Title>
      </Modal.Header >
      <Modal.Body>
        {!customFieldType && <>
          { parameterType === ParameterType.INPUT && subjectType === ParameterSubjectType.STEP &&
            <FromOutputParameterSelect subjectId={subjectId} linkedOutput={linkedOutput} handleOutputSelect={handleOutputSelect} />
          }
          { parameterType === ParameterType.INPUT && subjectType === ParameterSubjectType.OPERATION && operation &&
            <NewOperationParamSelect
              operation={operation}
              linkedOutput={linkedOutput}
              handleOutputSelect={handleOutputSelect}
            />
          }
        </>}
        <TitleAndIdentifierAndDescriptionFormInputs title={title} setTitle={setTitle} titleError={titleError} setTitleError={setTitleError} identifier={identifier} setIdentifier={setIdentifier} identifierError={identifierError} identifierOptional={subjectType !== ParameterSubjectType.OPERATION} setIdentifierError={setIdentifierError} description={description} setDescription={setDescription} subjectId={subjectId} subjectType={subjectType} parameterType={parameterType} />
        { !customFieldType && <div>
          <FieldTypeSelect
            disabled={!!linkedOutput || inputType === ParameterInputType.TABLE}
            fieldType={fieldType}
            onFieldSelect={onFieldSelect}
            typeOptions={allowedFieldTypes}
          />
          { fieldTypeError && <div className='text-danger'>{fieldTypeError}</div> }
          { subjectType !== ParameterSubjectType.PART && <InputTypeSelect
            subjectId={subjectId}
            inputType={inputType}
            setInputType={onInputTypeSelect}
            disabled={!!linkedOutput}
            parameterType={parameterType}
            allowChoice={allowChoice}
          /> }
        </div> }
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>{I18n.get('cancel')}</Button>
        <Button type="submit" variant="primary" onClick={() => void handleSave()}>{I18n.get('confirm')}</Button>
      </Modal.Footer>
    </Modal>}
    { newParameterId && <NonPrimitiveParameterModal parameterId={newParameterId} setShowEditValues={() => setNewParameterId(null)} runView={false} /> }
  </>
  );
}

function FromOutputParameterSelect(props: { subjectId: string, linkedOutput: SelectableParameter | undefined, handleOutputSelect: (param: SelectableParameter | undefined) => void }) {
  const { subjectId, linkedOutput, handleOutputSelect } = props;
  const operations = useTreeStore(state => state.operations);
  const parameters = useGetParametersToAssignFromOutput(subjectId);

  return (
    <div className='mb-3'>
      <Form.Label>{`${I18n.get('assignValueFrom')} (${I18n.get('optional')})`}</Form.Label>
        <GenericParameterSelect
          parameters={buildSelectableParameters(parameters, operations)}
          currentParameter={linkedOutput}
          createReference={handleOutputSelect}
          deleteReference={() => handleOutputSelect(undefined)}
        />
    </div>
  );
}

export function NewOperationParamSelect(props: { operation: Operation, linkedOutput?: SelectableParameter, handleOutputSelect: (parameter?: SelectableParameter) => void }) {
  const { operation, linkedOutput, handleOutputSelect } = props;
  const scenario = useTreeStore(state => state.containerLookup[operation.parentId]);
  const parameters = useGetParametersToAssignFromInput(operation, scenario?.parentId);

  return (
    <div className='my-3'>
      <Form.Label>{`${I18n.get('assignValueFrom')} (${I18n.get('optional')})`}</Form.Label>
      <GenericParameterSelect
        parameters={parameters}
        currentParameter={linkedOutput}
        createReference={handleOutputSelect}
        deleteReference={() => handleOutputSelect(undefined)}
      />
    </div>
  );
}

